import type { SupportSessionExchangeResponse, SupportSessionView } from "@eduviaos/shared";

/**
 * Phase SC-22 — super-admin support sessions in the IA.
 *
 * The support token lives in `sessionStorage`, which is scoped to this one
 * browser tab: a reload keeps the session, other tabs (including the SA's
 * own SA-portal tab) never see it. Nothing here touches the `eos_refresh`
 * cookie — that cookie belongs to the SA's own session.
 */

export const SUPPORT_SESSION_PATH = "/support-session";
const STORAGE_KEY = "eos.support_session.v1";

export interface StoredSupportSession {
  access_token: string;
  support_session: SupportSessionView;
}

export function isSupportSessionRoute(pathname: string = window.location.pathname): boolean {
  return pathname === SUPPORT_SESSION_PATH || pathname.startsWith(`${SUPPORT_SESSION_PATH}/`);
}

export function readSupportSession(tenantSlug: string | null): StoredSupportSession | null {
  let raw: string | null = null;
  try {
    raw = window.sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
  if (!raw) return null;
  try {
    const parsed = JSON.parse(raw) as Partial<StoredSupportSession>;
    const session = parsed.support_session;
    if (!parsed.access_token || !session?.expires_at || !session.tenant_id) {
      clearSupportSession();
      return null;
    }
    if (tenantSlug && session.tenant_id.toLowerCase() !== tenantSlug.toLowerCase()) {
      clearSupportSession();
      return null;
    }
    if (new Date(session.expires_at).getTime() <= Date.now()) {
      clearSupportSession();
      return null;
    }
    return { access_token: parsed.access_token, support_session: session };
  } catch {
    clearSupportSession();
    return null;
  }
}

export function storeSupportSession(resp: SupportSessionExchangeResponse): StoredSupportSession {
  const stored: StoredSupportSession = {
    access_token: resp.access_token,
    support_session: resp.support_session,
  };
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    // Private mode / quota: the session still works in memory for this page load.
  }
  return stored;
}

export function clearSupportSession(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // Storage unavailable — nothing persisted to clear.
  }
}

let capturedCode: string | null = null;

/**
 * Read the one-time code from `#code=…` and immediately strip the fragment
 * from the address bar and history so it can't be copied, bookmarked, or
 * replayed via Back. Idempotent: later calls return the captured value
 * (React StrictMode runs mount effects twice in development).
 */
export function takeSupportCodeFromHash(): string | null {
  const hash = window.location.hash.replace(/^#/, "");
  if (hash) {
    const code = new URLSearchParams(hash).get("code");
    if (code) capturedCode = code;
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + window.location.search,
    );
  }
  return capturedCode;
}

const inflight = new Map<string, Promise<SupportSessionExchangeResponse>>();

/** Exchange each code at most once per page load, whatever re-renders happen. */
export function exchangeOnce(
  code: string,
  exchange: (code: string) => Promise<SupportSessionExchangeResponse>,
): Promise<SupportSessionExchangeResponse> {
  const existing = inflight.get(code);
  if (existing) return existing;
  const promise = exchange(code);
  inflight.set(code, promise);
  return promise;
}

export function resetSupportSessionModuleForTests(): void {
  capturedCode = null;
  inflight.clear();
}
